import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Input, Select, Textarea } from "../components/ui";
import { ArrowLeft, PlusCircle, MapPin, Globe, Clock, DollarSign, QrCode, Hash, Upload, X, Lock, ArrowRight } from "lucide-react";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
};

export default function ClubCreationPage() {
  const nav = useNavigate();
  const { user, refreshMe } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitDetail, setLimitDetail] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    logo: "",
    description: "",
    city: "",
    country: "India",
    timezone: "Asia/Kolkata",
    currency: "INR",
    default_match_fee: "",
    upi_id: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const base64 = await fileToBase64(file);
      setFormData(prev => ({ ...prev, logo: base64 }));
    } catch (err) {
      console.error('Failed to convert image:', err);
      alert('Failed to process image');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        logo: formData.logo || null,
        currency: formData.currency,
        upi_id: formData.upi_id || null,
        description: formData.description || null,
        city: formData.city || null,
        country: formData.country || null,
        timezone: formData.timezone || "Asia/Kolkata",
        default_match_fee: formData.default_match_fee ? parseFloat(formData.default_match_fee) : 0,
      };
      await api.post("/clubs", payload);
      await refreshMe();
      // Force a re-render by waiting for state to propagate
      await new Promise(resolve => setTimeout(resolve, 50));
      toast.success("Club created!");
      nav("/home", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        setLimitDetail({
          message: detail.message || "Club limit reached",
          currentPlan: detail.current_plan || "your current plan",
          maxClubs: detail.max_clubs || 0,
          currentClubs: detail.current_clubs || 0,
        });
        setShowLimitModal(true);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const timezones = [
    "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
    "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Toronto", "America/Vancouver",
    "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
    "Africa/Johannesburg", "Africa/Cairo",
    "UTC"
  ];

  const countries = [
    "India", "Australia", "England", "South Africa", "New Zealand", "Pakistan", "Sri Lanka",
    "Bangladesh", "West Indies", "Afghanistan", "Ireland", "Zimbabwe", "Netherlands",
    "United Arab Emirates", "Oman", "Scotland", "USA", "Canada", "Singapore", "Hong Kong"
  ];

  return (
    <div className="app-shell flex flex-col min-h-screen bg-white">
      <div className="px-5 pt-6 pb-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center mb-4" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="h-12 w-12 rounded-2xl bg-brand-100 grid place-items-center mb-4">
          <PlusCircle size={26} className="text-brand" />
        </div>
        <h1 className="text-3xl font-bold">Create New Club</h1>
        <p className="text-ink-muted mt-1 text-[15px]">Set up your cricket community</p>
      </div>

      <div className="px-5 flex-1 overflow-y-auto pb-10">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto">
          <Card>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Hash size={18} className="text-brand" /> Basic Details
            </h3>
            <div className="space-y-4">
              <Input
                label="Club Name *"
                placeholder="Sunday Warriors CC"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="club-name-input"
              />
              <div className="space-y-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-1.5">
                    Club Logo
                  </span>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                        data-testid="club-logo-upload"
                        disabled={uploadingLogo}
                      />
                      <label
                        htmlFor="logo-upload"
                        className="cursor-pointer"
                      >
                        {formData.logo && formData.logo.startsWith('data:') ? (
                          <img src={formData.logo} alt="Club logo" className="h-20 w-20 rounded-xl object-cover border border-black/10" />
                        ) : formData.logo ? (
                          <img src={formData.logo} alt="Club logo" className="h-20 w-20 rounded-xl object-cover border border-black/10" />
                        ) : (
                          <div className="h-20 w-20 rounded-xl bg-brand-100 grid place-items-center border-2 border-dashed border-brand-300">
                            <Upload size={28} className="text-brand" />
                          </div>
                        )}
                      </label>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingLogo}
                        data-testid="club-logo-upload-btn"
                      >
                        <Upload size={16} /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
                      </Button>
                      {formData.logo && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, logo: "" }))}
                          className="text-red-500 hover:bg-red-50"
                          data-testid="club-logo-remove-btn"
                        >
                          <X size={14} /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </label>
              </div>
              <Textarea
                label="Description"
                placeholder="A friendly cricket club for weekend matches..."
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                data-testid="club-description-input"
              />
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <MapPin size={18} className="text-brand" /> Location
            </h3>
            <div className="space-y-4">
              <Input
                label="City"
                placeholder="Mumbai"
                name="city"
                value={formData.city}
                onChange={handleChange}
                data-testid="club-city-input"
              />
              <Select
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                data-testid="club-country-select"
              >
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
              <Select
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                data-testid="club-timezone-select"
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace("_", " ")}</option>
                ))}
              </Select>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-brand" /> Currency & Payments
            </h3>
            <div className="space-y-4">
              <Select
                label="Currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                data-testid="club-currency-select"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="AED">AED (د.إ)</option>
                <option value="SGD">SGD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="EUR">EUR (€)</option>
              </Select>
              <Input
                label="Default Match Fee"
                placeholder="500"
                type="number"
                step="0.01"
                min="0"
                name="default_match_fee"
                value={formData.default_match_fee}
                onChange={handleChange}
                data-testid="club-match-fee-input"
              />
              <Input
                label="Club UPI ID"
                placeholder="club@upi"
                name="upi_id"
                value={formData.upi_id}
                onChange={handleChange}
                data-testid="club-upi-input"
              />
            </div>
          </Card>

          <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="club-create-submit-btn">
            <PlusCircle size={18} />
            {loading ? "Creating…" : "Create Club"}
          </Button>
        </form>
      </div>

      {/* Club Limit Modal */}
      {showLimitModal && limitDetail && (
        <div className="fixed inset-0 z-50" data-testid="club-limit-modal-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowLimitModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-5">
          <div className="bg-white rounded-3xl p-6 pb-8 w-full max-w-[400px] shadow-xl animate-in zoom-in-95 fade-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-ink">Club Limit Reached</h3>
              <button onClick={() => setShowLimitModal(false)} className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10 grid place-items-center" aria-label="Close">
                <X size={20} className="text-ink-muted" />
              </button>
            </div>
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mx-auto mb-4">
                <Lock size={32} className="text-brand" />
              </div>
              <p className="text-sm text-ink-muted mb-2">{limitDetail.message}</p>
              <p className="text-sm text-ink-muted">
                Current plan: <span className="font-semibold text-ink">{limitDetail.currentPlan}</span>
              </p>
              <p className="text-sm text-ink-muted mt-1">
                Clubs: <span className="font-mono font-bold text-ink">{limitDetail.currentClubs}/{limitDetail.maxClubs}</span>
              </p>
            </div>
            <div className="space-y-3">
              <Button variant="primary" size="lg" className="w-full" onClick={() => { setShowLimitModal(false); nav("/pricing", { replace: true }); }} data-testid="club-limit-view-plans">
                <ArrowRight size={20} /> View Plans
              </Button>
              <Button variant="outline" size="lg" className="w-full" onClick={() => setShowLimitModal(false)} data-testid="club-limit-cancel">
                OK
              </Button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
